import { DASHBOARD_TODO_LIST_FEATURE_HINT_IDS } from '../featureHints';
import { AUTOMATIC_PRESENTATION_PRIORITIES } from './constants';
import { type AutomaticPresentationOptions } from './types';

// В каталоге order указан явно, чтобы при добавлении показа были видны все соседние позиции.
type AutomaticPresentationCatalogEntry = Pick<AutomaticPresentationOptions, 'id' | 'priority' | 'scope'> &
    Readonly<{ order: number }>;

/**
 * Общий каталог статических позиций автопоказов B2B. Новую поверхность добавляют сюда,
 * выбирая priority и order относительно существующих записей того же уровня.
 * Условия показа и готовность интерфейса задаёт владелец при вызове useAutomaticPresentation.
 * Каталог сгруппирован по priority, внутри уровня — по order.
 * Scope по умолчанию route; application сохраняет регистрацию между страницами.
 */
export const AUTOMATIC_PRESENTATIONS = {
    HIGH: {
        // TOP-1 по требованиям YSP-3480: https://st.yandex-team.ru/YSP-3480.
        priceIndexOzonOnboarding: {
            id: 'priceIndex.ozonOnboarding',
            priority: AUTOMATIC_PRESENTATION_PRIORITIES.HIGH,
            order: 100,
            scope: 'application',
        },
        // Напоминает о критичных ошибках, которые мешают работе магазина.
        dashboardCriticalAlerts: {
            id: 'dashboardAlerts.criticalAlerts',
            priority: AUTOMATIC_PRESENTATION_PRIORITIES.HIGH,
            order: 105,
            scope: 'application',
        },
        // Автоматически открывает Alty с сообщением для пользователя.
        altyNotification: {
            id: 'alty.notification',
            priority: AUTOMATIC_PRESENTATION_PRIORITIES.HIGH,
            order: 110,
            scope: 'application',
        },
    },
    NORMAL: {
        // Коммуникации в виде модального окна.
        communicationModal: {
            id: 'communication.modal',
            priority: AUTOMATIC_PRESENTATION_PRIORITIES.NORMAL,
            order: 90,
            scope: 'application',
        },
        // Знакомит с чек-листом рекомендаций для развития магазина.
        dashboardTodoListTitle: {
            id: DASHBOARD_TODO_LIST_FEATURE_HINT_IDS.TITLE,
            priority: AUTOMATIC_PRESENTATION_PRIORITIES.NORMAL,
            order: 100,
        },
        // Объясняет, как отмечать выполненные шаги и убирать их из активного списка.
        dashboardTodoListCompletionIcon: {
            id: DASHBOARD_TODO_LIST_FEATURE_HINT_IDS.COMPLETION_ICON,
            priority: AUTOMATIC_PRESENTATION_PRIORITIES.NORMAL,
            order: 110,
        },
        // Обращает внимание на новую рекомендацию по настройке и развитию магазина.
        dashboardTodoListNewLabel: {
            id: DASHBOARD_TODO_LIST_FEATURE_HINT_IDS.NEW_LABEL,
            priority: AUTOMATIC_PRESENTATION_PRIORITIES.NORMAL,
            order: 120,
        },
    },
    LOW: {
        // Приглашает в раздел новостей, когда есть непрочитанные сообщения.
        dashboardNews: {
            id: 'dashboardNews.unreadTooltip',
            priority: AUTOMATIC_PRESENTATION_PRIORITIES.LOW,
            order: 100,
            scope: 'application',
        },
        // Анонсирует тёмную тему и показывает, где выбрать оформление кабинета.
        themeFeatureHint: {
            id: 'changeTheme.featureHint',
            priority: AUTOMATIC_PRESENTATION_PRIORITIES.LOW,
            order: 110,
            scope: 'application',
        },
    },
} as const satisfies Record<
    keyof typeof AUTOMATIC_PRESENTATION_PRIORITIES,
    Record<string, AutomaticPresentationCatalogEntry>
>;
